import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import { useInvestigators } from "@/api/hooks/investigators";
import {
  DEBARMENT_STATUSES,
  DEBARMENT_STATUS_LABELS,
} from "@/api/labels";
import type { DebarmentStatus } from "@/api/types";
import { formatDate } from "@/utils/format";
import {
  EXPIRY_STATE_CHIP_COLOR,
  classifyExpiry,
} from "@/utils/trainingExpiry";

function ExpiryChip({ iso, label }: { iso: string | null; label: string }) {
  const state = classifyExpiry(iso);
  return (
    <Chip
      size="small"
      label={`${label} ${iso ? formatDate(iso) : "—"}`}
      color={EXPIRY_STATE_CHIP_COLOR[state]}
      variant="outlined"
    />
  );
}

export default function InvestigatorsList() {
  const [search, setSearch] = useState("");
  const [debarmentStatus, setDebarmentStatus] = useState<
    DebarmentStatus | ""
  >("");
  const [showInactive, setShowInactive] = useState(false);

  const { data, isLoading, error } = useInvestigators({
    search: search.trim() || undefined,
    debarment_status: (debarmentStatus || undefined) as
      | DebarmentStatus
      | undefined,
    is_active: showInactive ? undefined : true,
  });

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h5">Investigators</Typography>
        <Link to="new" style={{ textDecoration: "none" }}>
          <Button variant="contained" startIcon={<Add />}>
            New investigator
          </Button>
        </Link>
      </Box>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          size="small"
          label="Search name or NPI"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 240 }}
        />
        <TextField
          select
          size="small"
          label="Debarment status"
          value={debarmentStatus}
          onChange={(e) =>
            setDebarmentStatus(e.target.value as DebarmentStatus | "")
          }
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">All</MenuItem>
          {DEBARMENT_STATUSES.map((s) => (
            <MenuItem key={s} value={s}>
              {DEBARMENT_STATUS_LABELS[s]}
            </MenuItem>
          ))}
        </TextField>
        <FormControlLabel
          control={
            <Switch
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
          }
          label="Show inactive"
        />
      </Stack>

      {isLoading && <CircularProgress />}
      {error && <Alert severity="error">{error.message}</Alert>}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>NPI</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>GCP</TableCell>
              <TableCell>HIPAA</TableCell>
              <TableCell>Debarment</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data ?? []).map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>
                  <Link to={row.id}>
                    {row.first_name} {row.last_name}
                  </Link>
                </TableCell>
                <TableCell>{row.npi ?? "—"}</TableCell>
                <TableCell>{row.email ?? "—"}</TableCell>
                <TableCell>
                  <ExpiryChip iso={row.gcp_training_expires_at} label="GCP" />
                </TableCell>
                <TableCell>
                  <ExpiryChip iso={row.hipaa_training_expires_at} label="HIPAA" />
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={DEBARMENT_STATUS_LABELS[row.debarment_status]}
                    color={
                      row.debarment_status === "debarred"
                        ? "error"
                        : row.debarment_status === "clear"
                          ? "success"
                          : "default"
                    }
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  {row.is_active ? (
                    <Chip
                      size="small"
                      label="Active"
                      color="primary"
                      variant="outlined"
                    />
                  ) : (
                    <Chip
                      size="small"
                      label="Inactive"
                      color="default"
                      variant="outlined"
                    />
                  )}
                </TableCell>
              </TableRow>
            ))}
            {(data ?? []).length === 0 && !isLoading && (
              <TableRow>
                <TableCell
                  colSpan={7}
                  align="center"
                  sx={{ py: 4, color: "text.secondary" }}
                >
                  No investigators match the current filters.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
